'use client';

import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import ScrollReveal from '@/components/ScrollReveal';
import { useCart } from '@/components/CartContext';

const bestsellers = [
{
  id: 'prod-001',
  name: 'Luminous Veil Serum',
  category: 'Skincare',
  price: 89,
  originalPrice: null,
  rating: 4.9,
  reviews: 1204,
  badge: 'Bestseller',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_1a2817ff4-1767918438723.png',
  variant: '30ml',
  description: 'Brightening vitamin C serum with retinol complex'
},
{
  id: 'prod-002',
  name: 'Rose Petal Radiance Oil',
  category: 'Skincare',
  price: 72,
  originalPrice: 90,
  rating: 4.8,
  reviews: 380,
  badge: 'New',
  image: 'https://images.unsplash.com/photo-1669642216135-0b425871920d',
  variant: '50ml',
  description: 'Pure rosehip facial oil with hyaluronic acid'
},
{
  id: 'prod-003',
  name: 'Velvet Matte Lipstick',
  category: 'Makeup',
  price: 42,
  originalPrice: null,
  rating: 5.0,
  reviews: 2891,
  badge: 'Award Winner',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_143a8572f-1766147070684.png',
  variant: 'Rouge Noir',
  description: '12-hour wear, transfer-proof matte formula'
},
{
  id: 'prod-004',
  name: 'Midnight Repair Cream',
  category: 'Skincare',
  price: 124,
  originalPrice: null,
  rating: 4.9,
  reviews: 742,
  badge: 'Luxury',
  image: 'https://img.rocket.new/generatedImages/rocket_gen_img_168ab4107-1768930049198.png',
  variant: '50ml',
  description: 'Overnight regenerating cream with peptide complex'
}];


export default function BestsellersSection() {
  const { addItem } = useCart();

  const handleAddToCart = (product: (typeof bestsellers)[0]) => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      image: product.image,
      variant: product.variant,
      category: product.category
    });
  };

  return (
    <section className="section-padding bg-[#FAF8F3]">
      <div className="max-w-8xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <ScrollReveal>
          <div className="flex flex-col md:flex-row items-start md:items-end justify-between mb-16 gap-6">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-px bg-[#C9A96E]" />
                <span className="label-gold">Most Loved</span>
              </div>
              <h2
                className="font-fraunces leading-tight text-[#1A1612]"
                style={{ fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 400 }}>
                
                Bestselling
                <br />
                <em className="gradient-gold not-italic">Formulas</em>
              </h2>
            </div>
            <div className="flex flex-col items-start md:items-end gap-3">
              <p className="text-[14px] text-[#8A7E72] max-w-xs leading-relaxed text-left md:text-right">
                Curated by our beauty experts. Loved by thousands of customers worldwide.
              </p>
              <Link href="/products">
                <button className="btn-outline">View All Products</button>
              </Link>
            </div>
          </div>
        </ScrollReveal>

        {/* Product Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {bestsellers.map((product, index) =>
          <ScrollReveal key={product.id} delay={index * 100}>
              <div className="product-card group">
                {/* Image Container */}
                <div className="relative overflow-hidden bg-[#F0EBE0] aspect-product mb-4">
                  <AppImage
                  src={product.image}
                  alt={`${product.name} - ${product.description} by LuxeBeauty`}
                  className="product-image w-full h-full object-cover" />
                

                  {/* Badge */}
                  {product.badge &&
                <div
                  className="absolute top-3 left-3 px-2.5 py-1 text-[9px] font-bold tracking-widest uppercase"
                  style={{
                    background: product.badge === 'New' ? '#C9A96E' : '#1A1612',
                    color: product.badge === 'New' ? '#1A1612' : '#FAF8F3'
                  }}>
                  
                      {product.badge}
                    </div>
                }

                  {/* Quick Add Overlay */}
                  <div className="absolute inset-0 flex items-end p-4 bg-gradient-to-t from-[rgba(26,22,18,0.4)] to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    <button
                    onClick={() => handleAddToCart(product)}
                    className="quick-add w-full btn-gold text-[10px] py-3">
                    
                      + Add to Cart
                    </button>
                  </div>
                </div>

                {/* Product Info */}
                <Link href="/product-detail">
                  <div className="cursor-pointer">
                    <p className="label-gold mb-1" style={{ fontSize: '9px' }}>
                      {product.category}
                    </p>
                    <h3 className="font-fraunces text-[17px] font-medium text-[#1A1612] mb-1 hover:text-[#C9A96E] transition-colors">
                      {product.name}
                    </h3>
                    <p className="text-[12px] text-[#8A7E72] mb-3">{product.description}</p>

                    {/* Rating */}
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex gap-0.5">
                        {[1, 2, 3, 4, 5].map((s) =>
                      <span
                        key={s}
                        className={`text-[11px] ${s <= Math.floor(product.rating) ? 'text-[#C9A96E]' : 'text-[#E8D5B0]'}`}>
                        
                            ★
                          </span>
                      )}
                      </div>
                      <span className="text-[11px] text-[#8A7E72]">
                        ({product.reviews.toLocaleString()})
                      </span>
                    </div>

                    {/* Price */}
                    <div className="flex items-center gap-2">
                      <span className="font-fraunces text-[18px] font-medium text-[#1A1612]">
                        ${product.price}
                      </span>
                      {product.originalPrice &&
                    <span className="text-[13px] text-[#B8AFA6] line-through">
                          ${product.originalPrice}
                        </span>
                    }
                    </div>
                  </div>
                </Link>
              </div>
            </ScrollReveal>
          )}
        </div>

        {/* Marquee Banner */}
        <ScrollReveal className="mt-20">
          <div className="border-y border-[rgba(201,169,110,0.15)] py-5 overflow-hidden">
            <div className="flex animate-marquee gap-16 items-center whitespace-nowrap">
              {[...Array(2)].map((_, setIdx) =>
              [
              'Free Shipping Over $75',
              '100% Clean Ingredients',
              'Cruelty-Free Certified',
              'Dermatologist Tested',
              'Sustainably Sourced',
              'Vegan Formula',
              '30-Day Returns'].
              map((text) =>
              <div key={`${setIdx}-${text}`} className="flex items-center gap-4">
                    <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#8A7E72]">
                      {text}
                    </span>
                    <span className="text-[#C9A96E] text-lg">✦</span>
                  </div>
              )
              )}
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>);

}